import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { groupLifeService, type GroupLifeQuoteCalculation } from '@/services';
import { useQuery } from '@tanstack/react-query';
import { useQuotationVersionId } from './use-quotation-version-id.hook';

/**
 * Lê o resultado do CÁLCULO (prêmio) da cotação Group Life do snapshot do
 * InsureMO via `GET /portal/v1/group-life/quotes/:policyId`.
 *
 * O prêmio é gravado pelo back ao salvar o Step 4 (Comercialização), que
 * dispara `update → calculate → update` no InsureMO. Por isso a query é
 * sempre refetchada ao montar o Step 5 (Resumo) — assim o número exibido
 * reflete o último cálculo mesmo quando o usuário chega vindo direto do
 * Step 4 (sem reload da página).
 *
 * Compartilha a queryKey com `useHydrateGroupLifeQuote` (mesmo endpoint): o
 * cache é único e os demais campos (estipulante/plano/comercialização)
 * continuam sendo hidratados por aquele hook. Aqui só projetamos o bloco
 * `calculation` via `select`.
 */
export function useGroupLifeCalculation() {
  const { policyId } = useQuoteParams();
  const versionId = useQuotationVersionId();

  return useQuery({
    queryKey: versionId
      ? ['group-life', 'quote-at-version', policyId, versionId]
      : ['group-life', 'quote-details', policyId],
    queryFn: async () => {
      if (!policyId) throw new Error('policyId é obrigatório');
      const result = versionId
        ? await groupLifeService.getQuoteAtVersion(policyId, versionId)
        : await groupLifeService.getQuoteDetails(policyId);
      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Falha ao carregar o cálculo da cotação',
        );
      }
      return result.data;
    },
    enabled: !!policyId,
    select: (data): GroupLifeQuoteCalculation | undefined => data.calculation,
    // O cálculo muda no step anterior ao Resumo — buscamos sempre fresco
    // ao entrar no resumo em vez de servir o cache de 5 min do default.
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
}
